/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteOverlay } from 'src/components/layouts/RouteOverlay/RouteOverlay';
import { Divider, Checkbox, Tooltip as MuiTooltip } from '@mui/material';
import { FactsTable } from 'src/components/layouts/FactsTable/FactsTable';
import { OverlaySectionHeadline } from 'src/components/layouts/OverlaySectionHeadline/OverlaySectionHeadline';
import classNames from 'classnames';
import ProductImage from 'src/components/ui/ProductImage/ProductImage';
import { Address, useReturnReasonsQuery, useSingleReturnQuery, useUpdateRefundedState } from 'src/services/returns';
import Chat from 'src/components/layouts/Chat/Chat';
import logger from 'src/utils/logger';
import Image from 'src/components/ui/Image/Image';
import Icon from 'src/components/ui/Icon/Icon';
import { downloadAttachment } from 'src/services/utility';
import API_BASE_URL from 'src/constants';
import ImageGallery, { GalleryImage } from './ReturnImageGallery';
import styles from './ReturnOverlay.module.scss';
import ContainerBody from '../../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { getFormattedDay, getFormattedTime } from '../CasesOverview';
import { CaseDescription } from '../CaseOverlayContent';
import { ReturnStateBadge } from '../../components/layouts/ReturnsTable/ReturnsTable';
import ConfirmationModal from '../../components/modals/ConfirmationModal/ConfirmationModal';
import Button, { ButtonToneProps } from '../../components/ui/Button/Button';
import { ReturnLabelDownload } from './ReturnLabelDownload';

const AddressRenderer = ({ address }: { address: Address }) => {
  const strings = [address.street, address.street2, address.city, address.zip_code, address.country_name].filter(
    Boolean,
  );

  return (
    <div className="typo-text-14 text-faded">
      {strings.map((string, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`${index}-${string}`}>{string}</div>
      ))}
    </div>
  );
};

export function ReturnOverlay() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateRefundedState = useUpdateRefundedState();

  const { id } = useParams();
  const numericId = Number(id);

  if (!id && Number.isNaN(numericId)) {
    throw new Error(`Invalid contact ID: ${id}`);
  }
  const { data: returnData } = useSingleReturnQuery(numericId);
  const [editModeActive, setEditModeActive] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState<boolean>(!!returnData?.refunded);
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  useEffect(() => {
    setEditModeActive(false);
    setCheckboxValue(!!returnData?.refunded);
  }, [id]);

  const { data: reasons } = useReturnReasonsQuery();

  const handleRowClick = () => {
    if (returnData?.sale_order?.id) {
      navigate(`/outbound/${returnData.sale_order.id}`);
    }
  };

  const handleProductRowClick = (productId?: number) => {
    if (!productId) return;

    navigate(`/warehouse/inventory/${productId}`);
  };

  const handleEditClick = () => {
    setEditModeActive(true);
  };

  const handleSaveClick = () => {
    setIsSaveConfirmationOpen(true);
  };

  const handleConfirmClose = async (confirmed: boolean) => {
    if (confirmed && returnData?.id) {
      logger.info('Updating refunded state', returnData.id);
      try {
        await updateRefundedState.mutateAsync({
          params: {
            path: { return_picking_id: returnData.id },
          },
          body: {
            refunded: checkboxValue,
          },
        });

        setEditModeActive(false);
      } catch (error) {
        logger.error('Failed to update refunded state', error);
      }
    }

    setIsSaveConfirmationOpen(false);
  };

  return (
    <RouteOverlay title={returnData?.name}>
      <ContainerBody className="h-full flex flex-col gap-24">
        <div className="flex flex-col gap-12">
          <h2 className="text-full typo-title-36">{returnData?.name}</h2>
          {returnData?.date && (
            <span className="text-faded typo-text-14">
              {t('returns.created_on', {
                date: `${getFormattedDay(returnData.date)}, ${getFormattedTime(returnData.date)}`,
              })}
            </span>
          )}
        </div>

        <FactsTable
          entries={[[t('returns.status'), <ReturnStateBadge status={returnData?.status ? returnData?.status : ''} />]]}
        />
        <Divider />
        {returnData?.id && returnData.return_label && (
          <>
            <ReturnLabelDownload returnPickingId={returnData.id} label={returnData.return_label} />
            <Divider />
          </>
        )}
        <div>
          <OverlaySectionHeadline>{t('returns.sender')}</OverlaySectionHeadline>
          <FactsTable
            entries={[
              [t('general.name'), returnData?.partner?.name],
              [
                t('returns.address'),
                returnData?.partner?.address && <AddressRenderer address={returnData?.partner?.address} />,
              ],
              [t('returns.email'), returnData?.partner?.email],
              [t('returns.phone'), returnData?.partner?.phone],
            ]}
          />
        </div>
        <Divider />
        <div className="flex justify-between items-center px-2">
          <div className="flex items-center gap-2">
            <span className="text-faded typo-text-14">{t('returns.refunded')}</span>
            <Checkbox
              checked={checkboxValue}
              disabled={!editModeActive}
              onChange={e => setCheckboxValue(e.target.checked)}
            />
          </div>
          {editModeActive ? (
            <Button title={t('general.save')} tone={ButtonToneProps.PRIMARY_BLUE} onClick={handleSaveClick} />
          ) : (
            <Button title={t('general.edit')} tone={ButtonToneProps.SECONDARY} onClick={handleEditClick} />
          )}
        </div>
        <Divider />
        <div>
          <OverlaySectionHeadline>{t('returns.returned_products')}</OverlaySectionHeadline>
          <div className={classNames(styles.root, 'mb-16')}>
            <table className="table-auto w-full border-collapse border-spacing-0 surface-elevated">
              <thead>
                <tr>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('returns.name')}</th>
                  <th className="typo-text-12 text-faded py-2 px-4">
                    <div className="text-center">{t('returns.amount')}</div>
                  </th>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('returns.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {returnData?.returned_products?.map(product => {
                  return (
                    <tr
                      key={product.product_id}
                      className={`hover:surface-elevated-more cursor-pointer ${styles.row}`}
                      onClick={() => handleProductRowClick(product.product_id as number)}>
                      <td className="py-2 px-4">
                        <div className="flex items-center gap-x-8 md:gap-x-12">
                          {product && (
                            <>
                              <div className="hidden md:block">
                                <ProductImage id={product.product_id as number} />
                              </div>
                              <div>
                                <h4 className="typo-text-14--bold text-full">{product.product_name}</h4>
                                <div className="flex flex-col typo-text-12 text-faded">
                                  <span>
                                    {t('general.article_number_short')}: {product.default_code ?? '—'}
                                  </span>
                                  <span>
                                    {t('general.barcode')}: {product.barcode ?? '—'}
                                  </span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-4 typo-text-14 text-faded text-center">{product.quantity}</td>
                      <td className="py-2 px-4 typo-text-14 text-faded">
                        {product.reason_id
                          ? reasons?.find(reason => reason.id === product.reason_id)?.name ?? t('returns.no_reason')
                          : t('returns.no_reason')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <Divider />
        <div>
          <OverlaySectionHeadline>{t('returns.returned_order')}</OverlaySectionHeadline>
          <div className={classNames(styles.root, 'mb-16')}>
            <table className="table-auto w-full border-collapse border-spacing-0 surface-elevated">
              <thead>
                <tr>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('returns.name')}</th>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('tracking.order_date')}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  key={returnData?.sale_order?.id}
                  className={`hover:surface-elevated-more cursor-pointer ${styles.row}`}
                  onClick={handleRowClick}>
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-x-8 md:gap-x-12">
                      <div>
                        <h4 className="typo-text-14--bold text-full">{returnData?.sale_order?.name}</h4>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <h4 className="typo-text-14--bold text-full">
                      {returnData?.sale_order?.order_date
                        ? `${getFormattedDay(returnData.sale_order.order_date)}, ${getFormattedTime(returnData.sale_order.order_date)}`
                        : '-'}
                    </h4>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {(returnData?.note || (returnData?.attachments?.length ?? 0) > 0) && (
          <>
            <Divider />
            <div>
              <OverlaySectionHeadline>{t('returns.note')}</OverlaySectionHeadline>
              {returnData?.note && (
                <div className="mb-16">
                  <CaseDescription descriptionHtml={returnData.note} />
                </div>
              )}
              {(() => {
                const imageAttachments: GalleryImage[] =
                  returnData?.attachments
                    ?.filter(a => typeof a.id === 'number' && a.mimetype?.startsWith('image/'))
                    .map(a => ({ id: a.id as number, name: a.name })) ?? [];
                const otherAttachments =
                  returnData?.attachments?.filter(a => typeof a.id === 'number' && !a.mimetype?.startsWith('image/')) ??
                  [];

                return (
                  <>
                    {imageAttachments.length > 0 && (
                      <div className="mb-16">
                        <h4 className="typo-text-14--bold text-full mb-8">{t('general.attachments')}</h4>
                        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-12">
                          {imageAttachments.map((attachment, idx) => (
                            <MuiTooltip key={attachment.id} title={t('returns.open_gallery')}>
                              <button
                                type="button"
                                onClick={() => setGalleryIndex(idx)}
                                className="aspect-square overflow-hidden rounded-base border-subtle bg-surface-base transition-transform duration-150 hover:scale-105 hover:shadow-md">
                                <Image
                                  imageUrl={`${API_BASE_URL}portal/utility/attachments/${attachment.id}/view`}
                                  alt={attachment.name || `attachment-${attachment.id}`}
                                  className="w-full h-full object-cover"
                                  fallbackComponent={<Icon name="imageUpload" width={24} height={24} />}
                                />
                              </button>
                            </MuiTooltip>
                          ))}
                        </div>
                        <ImageGallery
                          images={imageAttachments}
                          index={galleryIndex ?? 0}
                          onIndexChange={setGalleryIndex}
                          isOpen={galleryIndex !== null}
                          onClose={() => setGalleryIndex(null)}
                        />
                      </div>
                    )}
                    {otherAttachments.length > 0 && (
                      <div className="mb-16">
                        {imageAttachments.length === 0 && (
                          <h4 className="typo-text-14--bold text-full mb-8">{t('general.attachments')}</h4>
                        )}
                        <div className="flex flex-col gap-8">
                          {otherAttachments.map(attachment => (
                            <button
                              type="button"
                              key={attachment.id}
                              onClick={() => downloadAttachment(attachment.id as number)}
                              className="flex items-center gap-8 px-12 py-8 rounded-base border-subtle hover:surface-elevated-more text-left">
                              <Icon name="download" width={20} height={20} />
                              <span className="typo-text-14 text-full break-all">
                                {attachment.name || `attachment-${attachment.id}`}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}
        <Divider />
        <div>{returnData?.id && <Chat model="stock.picking" res_id={Number(returnData.id)} />}</div>
      </ContainerBody>
      <ConfirmationModal
        isOpen={isSaveConfirmationOpen}
        title={t('returns.confirm_save')}
        description={t('returns.confirm_save_text')}
        onClose={handleConfirmClose}
      />
    </RouteOverlay>
  );
}
